/**
 * queryEntities.js — query entity parsing (NLP) for cacheService.
 * Extracted verbatim from services/cacheService.js (god-file decomposition).
 * Parses a free-text query into structured entities (teams, leagues, markets,
 * players, sportsbooks). parseQueryEntities is exported for cacheService's
 * queryWithCacheFallback; fuzzyTeamMatch / fastTeamLookup / getLeagueFromTeam
 * are internal helpers.
 */

import { TEAM_LOOKUP_MAP, CITY_TO_TEAMS, TEAM_ALIASES, AMBIGUOUS_TEAMS, MARKET_SPORT_HINTS, fuzzyMatchTeam, disambiguateTeam, getLeagueSport, findTeamsByCity, TEAM_TO_LEAGUE } from './teamResolution';

function fuzzyTeamMatch(input) {
  if (!input || input.length < 3) return null;
  
  const inputLower = input.toLowerCase();
  let bestMatch = null;
  let bestScore = 0;
  
  // Simple Levenshtein distance calculation
  const levenshtein = (a, b) => {
    if (a.length === 0) return b.length;
    if (b.length === 0) return a.length;
    
    const matrix = [];
    for (let i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }
    for (let j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }
    
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(
            matrix[i - 1][j - 1] + 1,
            matrix[i][j - 1] + 1,
            matrix[i - 1][j] + 1
          );
        }
      }
    }
    return matrix[b.length][a.length];
  };
  
  // Check against all known teams
  for (const [teamKey, teamInfo] of TEAM_LOOKUP_MAP.entries()) {
    const distance = levenshtein(inputLower, teamKey);
    const maxLen = Math.max(inputLower.length, teamKey.length);
    const similarity = 1 - (distance / maxLen);
    
    if (similarity > bestScore && similarity > 0.7) {
      bestScore = similarity;
      bestMatch = teamKey;
    }
  }
  
  if (bestMatch) {
    return { match: bestMatch, confidence: bestScore };
  }
  
  return null;
}

/**
 * 🚀 Fast team lookup - O(1) lookup with minimal processing
 * Used for matchup detection where we need quick lookups of both teams
 */
function fastTeamLookup(teamName) {
  const normalizedTeam = teamName.toLowerCase().trim();
  
  // Direct O(1) lookup
  if (TEAM_LOOKUP_MAP.has(normalizedTeam)) {
    return TEAM_LOOKUP_MAP.get(normalizedTeam);
  }
  
  // Check aliases
  if (TEAM_ALIASES[normalizedTeam]) {
    const canonical = TEAM_ALIASES[normalizedTeam];
    return TEAM_TO_LEAGUE[canonical] ? { ...TEAM_TO_LEAGUE[canonical], canonical } : null;
  }
  
  // Try fuzzy match for typos
  const fuzzyResult = fuzzyTeamMatch(normalizedTeam);
  if (fuzzyResult && fuzzyResult.confidence > 0.8) {
    const info = TEAM_LOOKUP_MAP.get(fuzzyResult.match.toLowerCase());
    return info || null;
  }
  
  return null;
}

/**
 * 🎯 OPTIMIZED: Get league info from team name with context awareness
 * Features:
 * - O(1) Map lookups
 * - Alias resolution
 * - Fuzzy matching for typos
 * - Context-aware disambiguation for ambiguous teams
 * - City name support
 */
function getLeagueFromTeam(teamName, queryContext = {}) {
  const { market, query = '' } = queryContext;
  const normalizedTeam = teamName.toLowerCase().trim();
  
  // 1️⃣ O(1) exact match via Map
  if (TEAM_LOOKUP_MAP.has(normalizedTeam)) {
    const info = TEAM_LOOKUP_MAP.get(normalizedTeam);
    
    // Check if this team is ambiguous
    if (AMBIGUOUS_TEAMS[info.canonical]) {
      return disambiguateTeam(info.canonical, { market, query });
    }
    
    return { league: info.league, sport: info.sport };
  }
  
  // 2️⃣ Check aliases (also O(1))
  if (TEAM_ALIASES[normalizedTeam]) {
    const canonical = TEAM_ALIASES[normalizedTeam];
    
    // Check if this team is ambiguous
    if (AMBIGUOUS_TEAMS[canonical]) {
      return disambiguateTeam(canonical, { market, query });
    }
    
    return TEAM_TO_LEAGUE[canonical] || null;
  }
  
  // 3️⃣ Check city names
  const cityTeams = findTeamsByCity(normalizedTeam);
  if (cityTeams.length > 0) {
    // If city has only one team for a sport matching the market, use that
    if (market) {
      const sportHint = Object.entries(MARKET_SPORT_HINTS).find(([k]) => 
        market.toLowerCase().includes(k)
      )?.[1];
      
      if (sportHint) {
        const matchingTeam = cityTeams.find(t => t.sport === sportHint);
        if (matchingTeam) {
          return { league: matchingTeam.league, sport: matchingTeam.sport };
        }
      }
    }
    
    // Return first team from city as default
    return { league: cityTeams[0].league, sport: cityTeams[0].sport };
  }
  
  // 4️⃣ Fuzzy matching for typos (only for longer inputs)
  if (normalizedTeam.length >= 4) {
    const fuzzyResult = fuzzyMatchTeam(normalizedTeam, 2);
    if (fuzzyResult && fuzzyResult.confidence >= 0.7) {
      
      // Check if fuzzy match is ambiguous
      if (AMBIGUOUS_TEAMS[fuzzyResult.match]) {
        return disambiguateTeam(fuzzyResult.match, { market, query });
      }
      
      return TEAM_TO_LEAGUE[fuzzyResult.match] || null;
    }
  }
  
  // 5️⃣ Partial match fallback (for compound names like "golden knights", "blue jackets")
  for (const [key, value] of Object.entries(TEAM_TO_LEAGUE)) {
    const keyLower = key.toLowerCase();
    if (normalizedTeam.includes(keyLower) || keyLower.includes(normalizedTeam)) {
      // Require at least 60% of the shorter string to match
      const minLength = Math.min(normalizedTeam.length, keyLower.length);
      const matchLength = keyLower.length;
      if (matchLength >= minLength * 0.6) {
        return value;
      }
    }
  }
  
  // No match found - return null to let AI handle it
  return null;
}

export function parseQueryEntities(prompt) {
  const lower = prompt.toLowerCase();
  
  // Detect query type
  let queryType = 'generic'; // 'player', 'team', 'generic'
  let player = null;
  let team = null;
  let market = null;
  let line = null;
  let direction = null; // 'over' or 'under'
  let league = null;  // 🆕 Add league detection
  let sport = null;   // 🆕 Add sport detection
  
  // ⭐⭐⭐ MATCHUP DETECTION: Handle "Team1 vs Team2" format first
  // This helps disambiguate leagues by finding common league between two teams
  const matchupPatterns = [
    /(.+?)\s+(?:vs\.?|versus|at|@)\s+(.+?)(?:\s+(?:moneyline|ml|spread|total|over|under|points?))?$/i,
    /(.+?)\s+(?:vs\.?|versus|at|@)\s+(.+)$/i
  ];
  
  let team1 = null, team2 = null;
  for (const pattern of matchupPatterns) {
    const match = prompt.match(pattern);
    if (match) {
      team1 = match[1].trim();
      team2 = match[2].trim();
      console.log(`[Parser] 🎯 Matchup detected: "${team1}" vs "${team2}"`);
      
      // Look up both teams to find common league
      const team1Info = fastTeamLookup(team1);
      const team2Info = fastTeamLookup(team2);
      
      console.log(`[Parser] Team1 "${team1}":`, team1Info);
      console.log(`[Parser] Team2 "${team2}":`, team2Info);
      
      if (team1Info && team2Info) {
        // Check if both teams share a league (for ambiguous teams)
        const team1Leagues = AMBIGUOUS_TEAMS[team1Info.canonical] || [team1Info.league];
        const team2Leagues = AMBIGUOUS_TEAMS[team2Info.canonical] || [team2Info.league];
        
        // Find intersection of leagues
        const commonLeagues = team1Leagues.filter(l => team2Leagues.includes(l));
        
        if (commonLeagues.length > 0) {
          // Use the common league
          league = commonLeagues[0];
          sport = getLeagueSport(league)?.sport;
          team = team1Info.canonical || team1;
          queryType = 'team';
          console.log(`[Parser] ⭐ Matchup disambiguated: "${team1}" vs "${team2}" → ${league.toUpperCase()} (common league)`);
        } else {
          // No common league, use the first team's league
          league = team1Info.league;
          sport = team1Info.sport;
          team = team1Info.canonical || team1;
          queryType = 'team';
          console.log(`[Parser] ⚠️ No common league, using team1: ${league?.toUpperCase()}`);
        }
      } else if (team1Info) {
        // Only team1 found
        league = team1Info.league;
        sport = team1Info.sport;
        team = team1Info.canonical || team1;
        queryType = 'team';
      } else if (team2Info) {
        // Only team2 found
        league = team2Info.league;
        sport = team2Info.sport;
        team = team2Info.canonical || team2;
        queryType = 'team';
      }
      break;
    }
  }
  
  // Market detection (what stat/bet type) - COMPREHENSIVE
  const marketPatterns = {
    // Player props - specific stats
    points: /\b(points?|pts?|score|scoring)\b/i,
    assists: /\b(assists?|ast|dimes?)\b/i,
    rebounds: /\b(rebounds?|reb|boards?)\b/i,
    threes: /\b(threes?|3s|three pointers?|3-pointers?|threes made)\b/i,
    blocks: /\b(blocks?|blk)\b/i,
    steals: /\b(steals?|stl)\b/i,
    turnovers: /\b(turnovers?|to)\b/i,
    'free-throws': /\b(free throws?|ft|free throws made)\b/i,
    'field-goals': /\b(field goals?|fg|field goals made)\b/i,
    'double-double': /\b(double[- ]double)\b/i,
    'triple-double': /\b(triple[- ]double)\b/i,
    
    // Combo props
    'points+assists': /\b(points?\s*(and|\+|plus)\s*assists?|pts?\s*(and|\+|plus)\s*ast|pa)\b/i,
    'points+rebounds': /\b(points?\s*(and|\+|plus)\s*rebounds?|pts?\s*(and|\+|plus)\s*reb|pr)\b/i,
    'points+assists+rebounds': /\b(points?\s*(and|\+|plus)\s*assists?\s*(and|\+|plus)\s*rebounds?|pts?\s*(and|\+|plus)\s*ast?\s*(and|\+|plus)\s*reb|par)\b/i,
    'assists+rebounds': /\b(assists?\s*(and|\+|plus)\s*rebounds?|ast?\s*(and|\+|plus)\s*reb|ar)\b/i,
    'rebounds+assists': /\b(rebounds?\s*(and|\+|plus)\s*assists?|reb?\s*(and|\+|plus)\s*ast|ra)\b/i,
    'steals+blocks': /\b(steals?\s*(and|\+|plus)\s*blocks?|stl?\s*(and|\+|plus)\s*blk|sb)\b/i,
    
    // Team markets
    moneyline: /\b(moneyline|ml|to win|winner|straight up|su)\b/i,
    spread: /\b(spread|line|handicap|point spread|[+-]\d+\.?\d*)\b/i,
    total: /\b(total|over\/under|o\/u|game total)\b/i,
    
    // NFL/Football specific
    'pass-yards': /\b(pass(ing)?\s*yards?|pass yds?|passing)\b/i,
    'rush-yards': /\b(rush(ing)?\s*yards?|rush yds?|rushing)\b/i,
    'rec-yards': /\b(rec(eiving)?\s*yards?|rec yds?|receiving)\b/i,
    receptions: /\b(receptions?|rec|catches?)\b/i,
    touchdowns: /\b(touchdowns?|td|tds|scores?)\b/i,
    interceptions: /\b(interceptions?|int|ints|picks?)\b/i,
    
    // NHL/Hockey specific
    goals: /\b(goals?)\b/i,
    saves: /\b(saves?)\b/i,
    'shots-on-goal': /\b(shots?\s*on\s*goal|sog)\b/i,
    
    // Baseball specific
    hits: /\b(hits?)\b/i,
    'home-runs': /\b(home\s*runs?|hr|homers?)\b/i,
    rbis: /\b(rbis?|runs?\s*batted\s*in)\b/i,
    strikeouts: /\b(strikeouts?|k|ks|strike\s*outs?)\b/i,
    
    // Generic/Catch-all
    'player-prop': /\b(player\s*prop)\b/i,
    'game-prop': /\b(game\s*prop)\b/i
  };
  
  // Detect market FIRST (so we don't confuse "Lakers Moneyline" as player name)
  for (const [marketName, pattern] of Object.entries(marketPatterns)) {
    if (pattern.test(lower)) {
      market = marketName;
      break;
    }
  }
  
  // 🆕 FALLBACK: If no market recognized, try to extract market keywords from query
  // This handles niche markets like "3rd quarter team total" that aren't in our patterns
  if (!market) {
    // Common market-related keywords that might indicate a market type
    const marketKeywords = ['quarter', 'qtr', 'half', 'period', 'innings?', 'moneyline', 'spread', 'total', 
                           'point', 'assist', 'rebound', 'block', 'steal', 'turnover', 'three', 'combo',
                           'double-double', 'triple-double', 'rush', 'pass', 'rec', 'touchdown', 'td',
                           'hit', 'home run', 'strikeout', 'goal', 'save', 'shot'];
    
    const foundKeywords = [];
    for (const keyword of marketKeywords) {
      const regex = new RegExp(`\\b${keyword}s?\\b`, 'i');
      if (regex.test(prompt)) {
        foundKeywords.push(keyword);
      }
    }
    
    // If we found market-related keywords, join them as the market search term
    if (foundKeywords.length > 0) {
      market = foundKeywords.join(' ');
      console.log(`[Parser] 🔍 No exact market match, using keywords: "${market}"`);
    }
  }
  
  // Line detection (10+, over 25.5, under 3.5, etc.)
  const linePatterns = [
    /(\d+\.?\d*)\s*\+/,  // "10+"
    /over\s+(\d+\.?\d*)/i,  // "over 25.5"
    /under\s+(\d+\.?\d*)/i,  // "under 3.5"
    /[ou]\s*(\d+\.?\d*)/i,  // "o25.5" or "u3.5"
    />\s*(\d+\.?\d*)/,  // ">10"
    /<\s*(\d+\.?\d*)/   // "<3.5"
  ];
  
  for (const pattern of linePatterns) {
    const match = lower.match(pattern);
    if (match) {
      line = parseFloat(match[1]);
      // Detect direction
      if (/over|[ou]\s*\d|>\s*\d|\d+\+/.test(lower)) {
        direction = 'over';
      } else if (/under|<\s*\d/.test(lower)) {
        direction = 'under';
      }
      break;
    }
  }
  
  // Team detection - help identify obvious teams (AI does heavy lifting, cache is source of truth)
  const teamNames = [
    // === NBA (30 teams) ===
    'Lakers', 'Celtics', 'Warriors', 'Heat', 'Nets', 'Bucks', 'Suns', 'Clippers',
    'Nuggets', 'Mavericks', 'Grizzlies', 'Pelicans', 'Kings', 'Timberwolves',
    'Thunder', 'Trail Blazers', 'Blazers', 'Jazz', 'Rockets', 'Spurs',
    'Hawks', 'Hornets', 'Bulls', 'Cavaliers', 'Cavs', 'Pistons', 'Pacers', 'Magic',
    '76ers', 'Sixers', 'Knicks', 'Raptors', 'Wizards',
    
    // === WNBA (12 teams) ===
    'Aces', 'Lynx', 'Liberty', 'Sun', 'Storm', 'Sky', 'Fever', 'Wings',
    'Sparks', 'Mercury', 'Mystics', 'Dream',
    
    // === NFL (32 teams) ===
    // AFC East
    'Bills', 'Dolphins', 'Patriots', 'Jets',
    // AFC North
    'Ravens', 'Bengals', 'Browns', 'Steelers',
    // AFC South
    'Texans', 'Colts', 'Jaguars', 'Titans',
    // AFC West
    'Broncos', 'Chiefs', 'Raiders', 'Chargers',
    // NFC East
    'Cowboys', 'Giants', 'Eagles', 'Commanders', 'Washington',
    // NFC North
    'Bears', 'Lions', 'Packers', 'Vikings',
    // NFC South
    'Falcons', 'Panthers', 'Saints', 'Buccaneers', 'Bucs',
    // NFC West
    '49ers', 'Niners', 'Cardinals', 'Rams', 'Seahawks',
    
    // === NCAAB (Top 100+ programs) ===
    // Power 5 - ACC
    'Duke', 'North Carolina', 'UNC', 'Virginia', 'Virginia Tech', 'Miami', 'Syracuse',
    'Louisville', 'Clemson', 'Notre Dame', 'NC State', 'Wake Forest', 'Boston College',
    'Florida State', 'FSU', 'Georgia Tech', 'Pittsburgh', 'Pitt',
    
    // Power 5 - Big Ten
    'Michigan', 'Michigan State', 'MSU', 'Ohio State', 'Purdue', 'Indiana', 'Illinois',
    'Wisconsin', 'Iowa', 'Maryland', 'Penn State', 'Minnesota', 'Nebraska', 'Northwestern',
    'Rutgers',
    
    // Power 5 - Big 12
    'Kansas', 'Baylor', 'Texas', 'Texas Tech', 'Oklahoma', 'Oklahoma State', 'Kansas State',
    'Iowa State', 'West Virginia', 'TCU',
    
    // Power 5 - SEC
    'Kentucky', 'Tennessee', 'Alabama', 'Auburn', 'Florida', 'Arkansas', 'LSU',
    'Mississippi State', 'Ole Miss', 'Missouri', 'Texas A&M', 'South Carolina', 'Georgia',
    'Vanderbilt',
    
    // Power 5 - Pac-12
    'UCLA', 'USC', 'Arizona', 'Oregon', 'Washington', 'Stanford', 'Colorado',
    'Utah', 'Arizona State', 'ASU', 'California', 'Cal', 'Oregon State', 'Washington State',
    
    // Other Major Programs
    'Gonzaga', 'Villanova', 'Creighton', 'Xavier', 'Butler', 'Marquette', 'Providence',
    'St. John\'s', 'Seton Hall', 'Georgetown', 'DePaul', 'UConn', 'Connecticut',
    'Memphis', 'Houston', 'Cincinnati', 'Wichita State', 'Temple', 'SMU', 'UCF',
    'BYU', 'Saint Mary\'s', 'San Diego State', 'SDSU', 'Nevada', 'Boise State', 'UNLV',
    'VCU', 'Rhode Island', 'Davidson', 'Dayton', 'Richmond', 'St. Bonaventure',
    
    // HBCUs
    'Morgan State', 'Howard', 'Hampton', 'Norfolk State', 'Delaware State',
    'North Carolina A&T', 'NC A&T', 'South Carolina State', 'Florida A&M', 'FAMU',
    'Bethune-Cookman', 'Coppin State', 'Maryland Eastern Shore', 'UMES',
    
    // === NCAAF (FBS - Top conferences) ===
    // Same teams as basketball but for football context
    // Power 5 football programs already covered above
    // Adding FBS-only notable programs
    'Air Force', 'Army', 'Navy', 'Marshall', 'Coastal Carolina', 'Appalachian State',
    'Liberty', 'James Madison', 'JMU', 'Troy', 'Louisiana', 'ULL',
    
    // === NHL (32 teams) ===
    // Atlantic
    'Bruins', 'Sabres', 'Red Wings', 'Panthers', 'Canadiens', 'Senators', 'Lightning', 'Maple Leafs',
    // Metropolitan
    'Hurricanes', 'Blue Jackets', 'Devils', 'Islanders', 'Rangers', 'Flyers', 'Penguins', 'Capitals',
    // Central
    'Blackhawks', 'Avalanche', 'Stars', 'Wild', 'Predators', 'Blues', 'Jets', 'Coyotes',
    // Pacific
    'Ducks', 'Flames', 'Oilers', 'Kings', 'Sharks', 'Kraken', 'Canucks', 'Golden Knights', 'Knights',
    
    // === MLB (30 teams) ===
    // AL East
    'Orioles', 'Red Sox', 'Yankees', 'Rays', 'Blue Jays',
    // AL Central
    'Guardians', 'Twins', 'White Sox', 'Royals', 'Tigers',
    // AL West
    'Astros', 'Angels', 'Athletics', 'A\'s', 'Mariners', 'Rangers',
    // NL East
    'Braves', 'Marlins', 'Mets', 'Phillies', 'Nationals',
    // NL Central
    'Cubs', 'Reds', 'Brewers', 'Pirates', 'Cardinals',
    // NL West
    'Diamondbacks', 'D-backs', 'Rockies', 'Dodgers', 'Padres', 'Giants'
  ];
  
  for (const teamName of teamNames) {
    if (new RegExp(`\\b${teamName}\\b`, 'i').test(prompt)) {
      team = teamName;
      queryType = 'team';
      
      // 🆕 Get league/sport from team name mapping
      const leagueInfo = getLeagueFromTeam(teamName, { market, prompt: lower });
      if (leagueInfo) {
        league = leagueInfo.league;
        sport = leagueInfo.sport;
        console.log(`[Parser] 🏟️ Team "${teamName}" → ${league.toUpperCase()} (${sport})`);
      }
      break;
    }
  }
  
  // 🆕 If no team found yet, check ALIASES (e.g., "avs" → "Avalanche", "pens" → "Penguins")
  if (!team) {
    for (const [alias, canonicalTeam] of Object.entries(TEAM_ALIASES)) {
      if (new RegExp(`\\b${alias}\\b`, 'i').test(lower)) {
        team = canonicalTeam;
        queryType = 'team';
        
        // Get league/sport from the canonical team
        const leagueInfo = getLeagueFromTeam(canonicalTeam, { market, prompt: lower });
        if (leagueInfo) {
          league = leagueInfo.league;
          sport = leagueInfo.sport;
          console.log(`[Parser] 🏟️ Alias "${alias}" → "${canonicalTeam}" → ${league.toUpperCase()} (${sport})`);
        }
        break;
      }
    }
  }
  
  // 🆕 If still no team, try city names
  if (!team) {
    for (const [city, cityTeams] of Object.entries(CITY_TO_TEAMS)) {
      if (new RegExp(`\\b${city}\\b`, 'i').test(lower)) {
        // Use the first team from the city (or better: use market hints)
        const firstTeam = cityTeams[0];
        if (firstTeam && TEAM_TO_LEAGUE[firstTeam]) {
          team = firstTeam;
          queryType = 'team';
          
          // Try to pick the right team based on market hints
          const leagueInfo = getLeagueFromTeam(city, { market, prompt: lower });
          if (leagueInfo) {
            league = leagueInfo.league;
            sport = leagueInfo.sport;
            // Find the team that matches this sport
            const matchingTeam = cityTeams.find(t => TEAM_TO_LEAGUE[t]?.sport === sport);
            if (matchingTeam) {
              team = matchingTeam;
            }
            console.log(`[Parser] 🏟️ City "${city}" → "${team}" → ${league.toUpperCase()} (${sport})`);
          }
        }
        break;
      }
    }
  }
  
  // 🆕 Also detect league from explicit keywords in query (e.g., "nhl avalanche", "nfl jets")
  if (!league) {
    const leagueKeywordMap = {
      'nba': { league: 'nba', sport: 'basketball' },
      'wnba': { league: 'wnba', sport: 'basketball' },
      'nfl': { league: 'nfl', sport: 'football' },
      'nhl': { league: 'nhl', sport: 'hockey' },
      'mlb': { league: 'mlb', sport: 'baseball' },
      'mls': { league: 'mls', sport: 'soccer' },
      'ncaab': { league: 'ncaab', sport: 'basketball' },
      'ncaaf': { league: 'ncaaf', sport: 'football' },
      'college basketball': { league: 'ncaab', sport: 'basketball' },
      'college football': { league: 'ncaaf', sport: 'football' },
      'hockey': { league: 'nhl', sport: 'hockey' },
      'baseball': { league: 'mlb', sport: 'baseball' },
      'soccer': { league: 'mls', sport: 'soccer' },
      'football': { league: 'nfl', sport: 'football' }, // Default football to NFL
    };
    
    for (const [keyword, info] of Object.entries(leagueKeywordMap)) {
      if (new RegExp(`\\b${keyword}\\b`, 'i').test(lower)) {
        league = info.league;
        sport = info.sport;
        console.log(`[Parser] 🏟️ League keyword "${keyword}" detected → ${league.toUpperCase()}`);
        break;
      }
    }
  }
  
  // Player detection - only if NOT a team query and NOT a team market
  // (Don't detect player if we already found a team OR if query is team-focused like "moneyline", "spread")
  if (!team && !['moneyline', 'spread', 'total'].includes(market)) {
    const words = prompt.split(/\s+/);
    const capitalizedWords = [];
    
    // First, try to detect capitalized names (original approach)
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      // Check if word starts with capital and has at least 3 chars
      if (/^[A-Z][a-z]{2,}/.test(word) && word.length >= 3) {
        // Exclude common words AND market keywords
        const excludeWords = ['Over', 'Under', 'Today', 'Tonight', 'Tomorrow', 'Game', 'Best', 'Good', 'Great', 
                               'Moneyline', 'Spread', 'Total', 'Points', 'Assists', 'Rebounds'];
        if (!excludeWords.includes(word)) {
          capitalizedWords.push(word);
        }
      }
    }
    
    // If we have 1-3 capitalized words, likely a player name
    if (capitalizedWords.length >= 1 && capitalizedWords.length <= 3) {
      player = capitalizedWords.join(' ');
      queryType = 'player';
    }
    
    // 🔧 FIX: Also detect player names from LOWERCASE queries
    // If query has a player prop market (points, assists, etc.) and a line, extract the name part
    if (!player && market && line !== null) {
      const playerPropMarkets = ['points', 'assists', 'rebounds', 'threes', 'blocks', 'steals', 'turnovers',
                                  'points+assists', 'points+rebounds', 'points+assists+rebounds', 'assists+rebounds',
                                  'pass-yards', 'rush-yards', 'rec-yards', 'receptions', 'touchdowns',
                                  'goals', 'saves', 'shots-on-goal', 'double-double', 'triple-double'];
      
      if (playerPropMarkets.includes(market)) {
        // Remove market keywords, numbers, and direction keywords to extract player name
        let namePart = lower
          .replace(/\b(points?|pts?|assists?|ast|rebounds?|reb|threes?|3s|blocks?|steals?|turnovers?|over|under|o\/u|\d+\.?\d*|\+|-)\b/gi, '')
          .replace(/\b(pass(ing)?|rush(ing)?|rec(eiving)?|yards?|yds?|touchdowns?|tds?|receptions?|goals?|saves?|shots?)\b/gi, '')
          .replace(/\s+/g, ' ')
          .trim();
        
        // If we have 1-3 words remaining, it's likely the player name
        const nameWords = namePart.split(/\s+/).filter(w => w.length >= 2);
        if (nameWords.length >= 1 && nameWords.length <= 4) {
          // Capitalize each word for consistent formatting
          player = nameWords.map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
          queryType = 'player';
        }
      }
    }
  }
  
  return {
    queryType,  // 'player', 'team', or 'generic'
    player,
    team,
    market,
    line,
    direction,
    league,     // 🆕 Detected league (e.g., 'nhl', 'nba', 'nfl')
    sport,      // 🆕 Detected sport (e.g., 'hockey', 'basketball', 'football')
    originalPrompt: prompt
  };
}
